// hooks/useProductSubmit.ts
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Product } from "@apis/index";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

export const useProductSubmit = (
  productData: any,
  user: any,
  currentStoreId?: any,
) => {
  const { data: lensaddons } = useSelector(
    (state: RootState) => state.lensaddons,
  );

  const navigate = useNavigate();
  const { storeId: paramStoreId, productId } = useParams();
  const storeId = currentStoreId || paramStoreId;

  const handleSubmit = useCallback(
    async (values: any) => {
      const formData: any = new FormData();

      // Helper to append only if value exists
      const appendIfValue = (key: string, value: any) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      };

      appendIfValue("name", values.name);
      appendIfValue("sku", values.productSKU);
      appendIfValue("price", values.price);

      if (!productData) {
        if (user.role === "store") {
          formData.append("storeId", user.id);
        } else {
          formData.append("storeId", storeId);
        }
      }

      appendIfValue("categoryId", values.category);

      if (Array.isArray(values.subCategory)) {
        if (values.subCategory.length > 0) {
          values.subCategory.forEach((id: string, index: number) => {
            formData.append(`subCategoryIds[${index}]`, id);
          });
        } else {
          formData.append("subCategoryIds", "[]");
        }
      }

      appendIfValue("brandId", values.brand);
      appendIfValue("type", values.foodType);
      appendIfValue("requiredNotes", values.note);
      appendIfValue("unit", values.unit);
      appendIfValue("isMostOrdered", values.mostorder?.toString());
      appendIfValue("isLatestArrival", values.latestarrival?.toString());
      appendIfValue("isPopular", values.popular?.toString());
      appendIfValue("showInApp", values.showInApp?.toString());
      appendIfValue(
        "prescriptionRequired",
        values.prescriptionRequired?.toString(),
      );
      appendIfValue("description", values.description);
      appendIfValue("deliveryAvailability", values.deliveryAvailability);
      if (values.deliveryAvailability === "regular")
        appendIfValue("estimatedDeliveryDay", values.estimatedDeliveryDay); // Joi validation says string is fine if it parses to number, removed Number() cast just in case or keep if API expects it. keeping as is usually safe for formData.
      appendIfValue("vehicleAvailabilty", values.vehicleAvailability);
      appendIfValue("useQueue", values.useQueue?.toString());

      // New fields from Joi schema - only send if values exist
      if (values.maxFourWheelerLimit && values.maxFourWheelerLimit !== "") {
        appendIfValue("fourWheelerLimit", Number(values.maxFourWheelerLimit));
      }
      if (values.maxTwoWheelerLimit && values.maxTwoWheelerLimit !== "") {
        appendIfValue("twoWheelerLimit", Number(values.maxTwoWheelerLimit));
      }

      if (values.video?.rawFile) {
        formData.append("video", values.video.rawFile);
      }

      // Lens Addon logic (preserved but wrapped in check if used)
      const storeLensAddons = Array.isArray(lensaddons) ? lensaddons : [];
      const existingLensAddons = Array.isArray(productData?.lensAddons)
        ? productData.lensAddons
        : [];

      const mergeLensAddonIds = (source: any[], reference: any[]) =>
        source.map((addon) => {
          const matchedRef = reference.find(
            (refAddon) => refAddon.addonId === addon.addonId,
          );

          const toArray = (value: any) => (Array.isArray(value) ? value : []);

          const mapOptions = (list: any[] = [], refList: any[] = []) =>
            toArray(list).map((option) => {
              const matchedRefOption = refList.find(
                (refOption) =>
                  refOption.addonOptionId === option.addonOptionId ||
                  refOption.id === option.id,
              );
              return {
                ...option,
                id: matchedRefOption?.id ?? option.id,
              };
            });

          const mapLensTypes = (list: any[] = [], refList: any[] = []) =>
            toArray(list).map((entry) => {
              const matchedRefLens = refList.find(
                (refLens) =>
                  refLens.addonLensTypeId === entry.addonLensTypeId ||
                  refLens.id === entry.id,
              );
              return {
                ...entry,
                id: matchedRefLens?.id ?? entry.id,
              };
            });

          return {
            ...addon,
            id: matchedRef?.id ?? addon.id,
            options: mapOptions(addon.options, matchedRef?.options ?? []),
            normals: mapLensTypes(addon.normals, matchedRef?.normals ?? []),
            polarizeds: mapLensTypes(
              addon.polarizeds,
              matchedRef?.polarizeds ?? [],
            ),
          };
        });

      const appendLensAddons = (
        addonsList: any[],
        includeExistingIds: boolean,
      ) => {
        addonsList.forEach((addon: any, index: number) => {
          const addonIdValue = addon.id ?? addon.recordId;
          if (includeExistingIds && addonIdValue) {
            formData.append(`lensAddons[${index}][id]`, addonIdValue);
          }
          formData.append(`lensAddons[${index}][addonId]`, addon.addonId);

          if (Array.isArray(addon.options)) {
            addon.options.forEach((option: any, optionIndex: number) => {
              const optionIdValue = option.id ?? option.recordId;
              if (includeExistingIds && optionIdValue) {
                formData.append(
                  `lensAddons[${index}][options][${optionIndex}][id]`,
                  optionIdValue,
                );
              }
              formData.append(
                `lensAddons[${index}][options][${optionIndex}][addonOptionId]`,
                option.addonOptionId,
              );
              formData.append(
                `lensAddons[${index}][options][${optionIndex}][price]`,
                option.price,
              );
            });
          }

          const appendLensType = (
            list: any[],
            key: "normals" | "polarizeds",
          ) => {
            if (!Array.isArray(list)) return;

            list.forEach((option: any, optionIndex: number) => {
              const lensRecordId = option.id ?? option.recordId;
              if (includeExistingIds && lensRecordId) {
                formData.append(
                  `lensAddons[${index}][${key}][${optionIndex}][id]`,
                  lensRecordId,
                );
              }
              formData.append(
                `lensAddons[${index}][${key}][${optionIndex}][addonLensTypeId]`,
                option.addonLensTypeId,
              );
              formData.append(
                `lensAddons[${index}][${key}][${optionIndex}][price]`,
                option.price,
              );
            });
          };

          appendLensType(addon.normals, "normals");
          appendLensType(addon.polarizeds, "polarizeds");
        });
      };

      if (!!productData?.id) {
        const lensAddonsPayload = storeLensAddons.length
          ? mergeLensAddonIds(storeLensAddons, existingLensAddons)
          : existingLensAddons;
        if (lensAddonsPayload.length) {
          appendLensAddons(lensAddonsPayload, true);
        }
      } else {
        if (storeLensAddons.length) {
          appendLensAddons(storeLensAddons, false);
        }
      }
      // END LENS LOGIC

      // Addons
      if (values.addons && values.addons.length > 0) {
        formData.append(
          "addons",
          JSON.stringify(
            values.addons.map((addon: any) =>
              !!productData?.id
                ? (({ id, ...addonWithoutId }) => ({
                    ...addonWithoutId,
                    options: addon.options?.map(
                      ({ id: optionId, ...option }: any) => option,
                    ),
                  }))(addon)
                : addon,
            ),
          ),
        );
      }

      // Variations
      if (values.variants && values.variants.length > 0) {
        values.variants.forEach((variation: any, index: number) => {
          if (!!productData?.id || user.role != "store") {
            if (variation.id)
              formData.append(
                `variations[${index}][id]`,
                JSON.stringify(variation.id),
              );
          }
          formData.append(
            `variations[${index}][color]`,
            JSON.stringify({
              name: variation?.name || variation?.colorName || "",
              code: variation?.code || variation.colorCode || "",
            }),
          );
          formData.append(
            `variations[${index}][attributes]`,
            JSON.stringify(variation.attributes),
          );
          formData.append(
            `variations[${index}][price]`,
            variation.price.toString(),
          );
          if (variation.discountType) {
            formData.append(
              `variations[${index}][discountType]`,
              variation.discountType,
            );
          }
          if (variation.discount) {
            formData.append(
              `variations[${index}][discount]`,
              variation.discount.toString(),
            );
          }
          formData.append(
            `variations[${index}][stock]`,
            variation.stock.toString(),
          );
          formData.append(`variations[${index}][sku]`, variation.sku);
          if (variation.image?.rawFile) {
            formData.append(
              `variations[${index}][image]`,
              variation.image.rawFile,
            );
          }
          if (productData?.variations?.[index]?.image) {
            formData.append(
              `variations[${index}][oldImage]`,
              productData.variations[index].image,
            );
          }
        });
      }

      // Discount Infos
      if (values.discountType && values.discount) {
        const discountInfo: any = {
          type: values.discountType === "1" ? "percentage" : "amount",
          discount: values.discount,
          availability:
            values.discountAvailabilty === "1" ? "always" : "duration",
        };

        if (values.discountAvailabilty === "2") {
          if (values.discountstartdate)
            discountInfo.startDate = values.discountstartdate;
          if (values.discountenddate)
            discountInfo.endDate = values.discountenddate;
        }

        formData.append("discountInfos", JSON.stringify(discountInfo));
      }

      // Stock Infos
      if (values.stockType && values.availableDuringOpening) {
        const stockInfo: any = {
          type: values.stockType,
          availability:
            values.availableDuringOpening === "1" ? "opening" : "custom",
        };

        if (values.stockType !== "unlimited" && values.itemStock) {
          stockInfo.stock = Number(values.itemStock);
        }

        stockInfo.maxPurchaseLimit = Number(values.purchaseLimit) || 15;

        if (values.availableDuringOpening === "2") {
          if (values.availableTimeStart)
            stockInfo.startTime = values.availableTimeStart;
          if (values.availableTimeEnd)
            stockInfo.endTime = values.availableTimeEnd;
        }

        formData.append("stockInfos", JSON.stringify(stockInfo));
      }

      // SEO Infos
      const seoInfo: any = {};
      if (values.title) seoInfo.title = values.title;
      if (values.descriptionMeta) seoInfo.description = values.descriptionMeta;
      if (values.keyword) seoInfo.keywords = values.keyword;
      if (values.tags && values.tags.length > 0) seoInfo.tags = values.tags;

      if (Object.keys(seoInfo).length > 0) {
        formData.append("seoInfos", JSON.stringify(seoInfo));
      }

      if (values.image && values.image.length > 0) {
        values.image.forEach((file: any) => {
          if (file.rawFile) {
            formData.append(`images`, file.rawFile);
          }
        });
      }

      if (values.og?.rawFile) {
        formData.append("ogImage", values.og.rawFile);
      }

      const isUpdate = !!productData?.id;
      const useQueue = values.useQueue === "true" || values.useQueue === true;

      if (useQueue && !isUpdate) {
        // For background creation, don't wait for the promise to finish in a blocking way
        Product.create({ state: "products", data: formData })
          .then((result: any) => {
            toast.success(
              "Background upload started! Check progress in the history view.",
            );
            if (user?.role === "store") {
              navigate("/store/product-management/products");
            } else {
              navigate(-1);
            }
          })
          .catch((error: any) => {
            toast.error("Failed to initiate background upload.");
            console.error(error);
          });
        return;
      }

      const promise = isUpdate
        ? Product.update({
            state: "products",
            id: productData.id,
            data: formData,
          })
        : Product.create({ state: "products", data: formData });

      try {
        await toast.promise(promise, {
          pending: productData ? "Updating product..." : "Creating Product",
          success: ``,
          error: `Failed to ${productData ? "update" : "create"} product!`,
        });

        const result = await promise;
        console.log("✅ Product update successful, result:", result);
        console.log("📍 Navigation data:", {
          userRole: user?.role,
          storeId,
          productDataStoreId: productData?.storeId,
          productDataStore: productData?.store?.id,
        });

        if (user?.role === "store") {
          navigate("/store/product-management/products");
        } else {
          navigate(-1);
          // const targetStoreId = productData?.store?.id;
          // console.log("🎯 Target store ID for navigation:", targetStoreId);
          // if (!targetStoreId) {
          //   console.error("Could not determine store ID for navigation");
          //   toast.warning(
          //     "Product updated, but could not navigate back to store.",
          //   );
          //   return;
          // }
          // navigate(
          //   `/admin/users/store/store-lists/view/${targetStoreId}?module=Food&tab=Products`,
          // );
        }
      } catch (error) {
        console.error("API call FAILED:", error);
      }
    },
    [productData, user, storeId, navigate, lensaddons],
  );

  return { handleSubmit };
};
